export const HERO_AUTOPLAY_DELAY = 5000;
export const HERO_TRANSITION_DURATION = 850;

export function nextHeroIndex(currentIndex, slideCount, direction) {
  if (slideCount < 1) return 0;
  return (currentIndex + direction + slideCount) % slideCount;
}

export function shouldHeroAutoplay({
  reducedMotion,
  intersecting,
  documentVisible,
  focused,
  hovered,
  slideCount,
}) {
  return (
    !reducedMotion
    && intersecting
    && documentVisible
    && !focused
    && !hovered
    && slideCount > 1
  );
}

export class SparkProductHero {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-spark-hero-track]');
    this.slides = [...root.querySelectorAll('[data-spark-hero-slide]')];
    this.previous = root.querySelector('[data-spark-hero-prev]');
    this.next = root.querySelector('[data-spark-hero-next]');
    this.status = root.querySelector('[data-spark-hero-status]');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.currentIndex = 0;
    this.intersecting = true;
    this.focused = false;
    this.hovered = false;
    this.timer = 0;
    this.pointerStartX = null;
    this.resizeFrame = 0;
    this.observer = null;
  }

  init() {
    if (!this.track || !this.previous || !this.next || !this.status || !this.slides.length) return;

    const hasMultipleSlides = this.slides.length > 1;
    this.previous.hidden = !hasMultipleSlides;
    this.next.hidden = !hasMultipleSlides;
    this.previous.disabled = !hasMultipleSlides;
    this.next.disabled = !hasMultipleSlides;

    this.previous.addEventListener('click', (event) => this.handleControlClick(-1, event));
    this.next.addEventListener('click', (event) => this.handleControlClick(1, event));
    this.track.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { passive: true });
    this.track.addEventListener('pointerup', (event) => this.handlePointerUp(event), { passive: true });
    this.track.addEventListener('pointercancel', () => this.handlePointerCancel(), { passive: true });

    this.root.addEventListener('mouseenter', () => {
      this.hovered = true;
      this.stopAutoplay();
    });
    this.root.addEventListener('mouseleave', () => {
      this.hovered = false;
      this.restartAutoplay();
    });
    this.root.addEventListener('focusin', () => this.updateFocusState());
    this.root.addEventListener('focusout', () => {
      window.setTimeout(() => this.updateFocusState(), 0);
    });

    document.addEventListener('visibilitychange', () => this.restartAutoplay());
    this.motionQuery.addEventListener('change', () => {
      this.root.classList.toggle('spark-product-hero--reduced-motion', this.motionQuery.matches);
      this.restartAutoplay();
    });
    window.addEventListener('resize', () => this.handleResize(), { passive: true });
    document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event));

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.intersecting = entry.isIntersecting;
          this.restartAutoplay();
        },
        { threshold: 0.35 },
      );
      this.observer.observe(this.root);
    }

    this.root.classList.toggle('spark-product-hero--reduced-motion', this.motionQuery.matches);
    this.goTo(0, false, true);
    this.restartAutoplay();
  }

  move(direction, manual = true) {
    this.goTo(nextHeroIndex(this.currentIndex, this.slides.length, direction), manual);
  }

  handleControlClick(direction, event) {
    this.move(direction);
    if (event.detail > 0) {
      event.currentTarget.blur();
    }
  }

  goTo(index, manual = true, immediate = false) {
    this.currentIndex = Math.max(0, Math.min(this.slides.length - 1, index));
    const slide = this.slides[this.currentIndex];
    if (!slide) return;

    this.track.style.transitionDuration = (immediate || this.motionQuery.matches)
      ? '0ms'
      : `${HERO_TRANSITION_DURATION}ms`;
    this.track.style.transform = `translate3d(${-slide.offsetLeft}px, 0, 0)`;

    this.slides.forEach((item, itemIndex) => {
      const active = itemIndex === this.currentIndex;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', String(!active));
      item.querySelectorAll('a, button').forEach((control) => {
        control.tabIndex = active ? 0 : -1;
      });
    });

    const name = slide.dataset.heroTitle || `Product ${this.currentIndex + 1}`;
    this.status.textContent = `${name}, slide ${this.currentIndex + 1} of ${this.slides.length}`;
    if (manual) this.restartAutoplay();
  }

  handlePointerDown(event) {
    this.pointerStartX = event.clientX;
    this.stopAutoplay();
  }

  handlePointerUp(event) {
    if (this.pointerStartX === null) return;
    const distance = event.clientX - this.pointerStartX;
    this.pointerStartX = null;

    if (Math.abs(distance) >= 42) {
      this.move(distance < 0 ? 1 : -1);
    } else {
      this.restartAutoplay();
    }
  }

  handlePointerCancel() {
    this.pointerStartX = null;
    this.restartAutoplay();
  }

  handleResize() {
    window.cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = window.requestAnimationFrame(() => this.goTo(this.currentIndex, false, true));
  }

  updateFocusState() {
    this.focused = Boolean(this.root.querySelector(':focus-visible'));
    this.restartAutoplay();
  }

  handleBlockSelect(event) {
    const selected = event.target?.closest?.('[data-hero-block-id]');
    if (!selected || !this.root.contains(selected)) return;
    const index = this.slides.indexOf(selected);
    if (index < 0) return;

    this.stopAutoplay();
    this.goTo(index, false);
  }

  autoplayState() {
    return {
      reducedMotion: this.motionQuery.matches,
      intersecting: this.intersecting,
      documentVisible: document.visibilityState === 'visible',
      focused: this.focused,
      hovered: this.hovered,
      slideCount: this.slides.length,
    };
  }

  stopAutoplay() {
    window.clearTimeout(this.timer);
    this.timer = 0;
  }

  restartAutoplay() {
    this.stopAutoplay();
    if (!shouldHeroAutoplay(this.autoplayState())) return;

    this.timer = window.setTimeout(() => {
      this.move(1, false);
      this.restartAutoplay();
    }, HERO_AUTOPLAY_DELAY);
  }
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-spark-product-hero]').forEach((root) => {
    new SparkProductHero(root).init();
  });
}
